import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Users, Calendar, Clock, AlertTriangle, ArrowRightLeft, BarChart3, RefreshCw, Plus, Edit2, Trash2, ChevronLeft, ChevronRight, Ship, Search, UserCheck, UserX, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { DocumentUploadPanel } from '../components/DocumentUploadPanel';

const API = window.location.origin;

const TABS = [
  { id: 'cadastro', label: 'Tripulantes Cadastrados', icon: Users },
  { id: 'escala', label: 'Escala Operacional', icon: Calendar },
  { id: 'folgas', label: 'Folgas Programadas', icon: Clock },
  { id: 'afastados', label: 'Afastados', icon: UserX },
  { id: 'substituicoes', label: 'Substituicoes', icon: ArrowRightLeft },
];

const STATUS_COLORS = {
  ativo: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  afastado: 'bg-red-500/15 text-red-400 border-red-500/30',
  em_folga: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  inativo: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
};

const SETOR_COLORS = {
  Navegacao: 'bg-blue-500/15 text-blue-400',
  Maquinas: 'bg-orange-500/15 text-orange-400',
  Conves: 'bg-cyan-500/15 text-cyan-400',
  'Servicos de Bordo': 'bg-purple-500/15 text-purple-400',
};

const SITES = ['Todos', 'RJ', 'PE', 'PA'];
const SITE_LABELS = { 'Todos': 'Todos', 'RJ': 'Rio de Janeiro', 'PE': 'Pernambuco', 'PA': 'Para' };
const BASE_LABELS = { 'RJ': 'RJ', 'PE': 'Pernambuco', 'PA': 'PA' };

export default function EscalaServicosPage() {
  const { token, user, hasRole } = useAuth();
  const isAdmin = hasRole(['admin']);
  const [tab, setTab] = useState('cadastro');
  const [tripulantes, setTripulantes] = useState([]);
  const [servicos, setServicos] = useState({ items: [], total: 0 });
  const [folgas, setFolgas] = useState([]);
  const [afastamentos, setAfastamentos] = useState([]);
  const [substituicoes, setSubstituicoes] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [dataFiltro, setDataFiltro] = useState('2025-06-01');
  const [setorFiltro, setSetorFiltro] = useState('');
  const [siteFiltro, setSiteFiltro] = useState('Todos');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editTrip, setEditTrip] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, rRes, aRes, alRes] = await Promise.all([
        fetch(`${API}/api/escala/tripulantes`, { headers }),
        fetch(`${API}/api/escala/resumo?embarcacao=Navio Apoio I`, { headers }),
        fetch(`${API}/api/escala/afastamentos?embarcacao=Navio Apoio I`, { headers }),
        fetch(`${API}/api/escala/alertas?embarcacao=Navio Apoio I`, { headers }),
      ]);
      setTripulantes(await tRes.json());
      setResumo(await rRes.json());
      setAfastamentos(await aRes.json());
      setAlertas(await alRes.json());
    } catch { toast.error('Erro ao carregar dados'); }
    finally { setLoading(false); }
  }, [token]);

  const fetchServicos = useCallback(async () => {
    try {
      let url = `${API}/api/escala/servicos?embarcacao=Navio Apoio I&page=${page}&limit=50`;
      if (dataFiltro) url += `&data=${dataFiltro}`;
      if (setorFiltro) url += `&setor=${setorFiltro}`;
      const r = await fetch(url, { headers });
      setServicos(await r.json());
    } catch {}
  }, [token, dataFiltro, setorFiltro, page]);

  const fetchFolgas = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/escala/folgas?embarcacao=Navio Apoio I`, { headers });
      setFolgas(await r.json());
    } catch {}
  }, [token]);

  const fetchSubs = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/escala/substituicoes?embarcacao=Navio Apoio I`, { headers });
      setSubstituicoes(await r.json());
    } catch {}
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (tab === 'escala') fetchServicos(); }, [tab, fetchServicos]);
  useEffect(() => { if (tab === 'folgas') fetchFolgas(); }, [tab, fetchFolgas]);
  useEffect(() => { if (tab === 'substituicoes') fetchSubs(); }, [tab, fetchSubs]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const r = await fetch(`${API}/api/escala/seed`, { method: 'POST', headers });
      const d = await r.json();
      toast.success(d.message);
      fetchAll();
    } catch { toast.error('Erro'); }
    finally { setSeeding(false); }
  };

  const handleDeleteTrip = async (id) => {
    if (!window.confirm('Remover tripulante?')) return;
    await fetch(`${API}/api/escala/tripulantes/${id}`, { method: 'DELETE', headers });
    toast.success('Removido');
    fetchAll();
  };

  const handleSaveTrip = async (formData) => {
    const method = editTrip ? 'PUT' : 'POST';
    const url = editTrip ? `${API}/api/escala/tripulantes/${editTrip.id}` : `${API}/api/escala/tripulantes`;
    const r = await fetch(url, { method, headers, body: JSON.stringify(formData) });
    if (r.ok) {
      toast.success(editTrip ? 'Atualizado' : 'Cadastrado');
      setShowForm(false);
      setEditTrip(null);
      fetchAll();
    } else {
      const err = await r.json();
      toast.error(err.detail || 'Erro');
    }
  };

  // Site filter helper
  const filterBySite = (items, siteKey = 'base') => {
    if (siteFiltro === 'Todos') return items;
    return items.filter(i => (i[siteKey] || '').toUpperCase() === siteFiltro.toUpperCase());
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-cyan-400 font-mono">Carregando escala...</div></div>;

  const isEmpty = tripulantes.length === 0;

  const filteredTrips = filterBySite(tripulantes);
  const filteredServicos = filterBySite(servicos.items || []);
  const filteredFolgas = filterBySite(folgas);
  const filteredAfastamentos = filterBySite(afastamentos);
  const filteredSubs = siteFiltro === 'Todos' ? substituicoes : substituicoes.filter(s => {
    const trip = tripulantes.find(t => t.nome === s.tripulante_afastado);
    return trip && trip.base?.toUpperCase() === siteFiltro.toUpperCase();
  });

  return (
    <div className="space-y-5" data-testid="escala-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Ship className="w-6 h-6 text-blue-400" />Escala de Servicos Maritimos</h1>
          <p className="text-sm text-slate-500 mt-0.5">Navio Apoio I | Modelos 28x28, 40x10 e 7x7</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={fetchAll} className="h-7 text-xs"><RefreshCw className="w-3 h-3 mr-1" />Atualizar</Button>
          {isEmpty && isAdmin && <Button size="sm" onClick={handleSeed} disabled={seeding} className="bg-cyan-600 hover:bg-cyan-500 h-7 text-xs" data-testid="seed-escala">{seeding ? 'Populando...' : 'Popular Escala'}</Button>}
        </div>
      </div>

      {/* Resumo Cards */}
      {resumo && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="escala-resumo">
          {[
            { label: 'Cadastrados', value: resumo.total_cadastrados, color: 'text-white' },
            { label: 'Fixos', value: resumo.total_fixos, color: 'text-blue-400' },
            { label: 'Substitutos', value: resumo.total_substitutos, color: 'text-purple-400' },
            { label: 'Embarcados', value: resumo.embarcados, color: 'text-emerald-400' },
            { label: 'Em Folga', value: resumo.em_folga, color: 'text-amber-400' },
            { label: 'Afastados', value: resumo.afastados, color: 'text-red-400' },
            { label: 'Alertas', value: resumo.alertas_pendentes, color: resumo.alertas_pendentes > 0 ? 'text-red-400' : 'text-slate-400' },
          ].map((c, i) => (
            <div key={i} className="bg-slate-900/80 border border-slate-800 rounded-lg p-2.5 text-center">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">{c.label}</p>
              <p className={`text-lg font-bold font-mono ${c.color}`}>{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Alertas */}
      {alertas.length > 0 && (
        <div className="bg-red-900/10 border border-red-800/30 rounded-lg p-3" data-testid="escala-alertas">
          <h3 className="text-xs font-semibold text-red-400 uppercase mb-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Alertas ({alertas.length})</h3>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {alertas.map((a, i) => <p key={i} className="text-xs text-red-300/80">{a.icone} {a.mensagem}</p>)}
          </div>
        </div>
      )}

      {/* Tabs + Site Filter */}
      <div className="flex items-center justify-between border-b border-slate-800 pb-px">
        <div className="flex gap-1 overflow-x-auto" data-testid="escala-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors ${tab === t.id ? 'bg-slate-800 text-white border-b-2 border-cyan-500' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'}`}>
                <Icon className="w-3.5 h-3.5" />{t.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          <span className="text-[10px] text-slate-500 uppercase">Site:</span>
          {SITES.map(s => (
            <button key={s} onClick={() => setSiteFiltro(s)} data-testid={`site-${s}`} translate="no"
              className={`px-2 py-1 text-xs rounded transition ${siteFiltro === s ? 'bg-cyan-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}>{SITE_LABELS[s] || s}</button>
          ))}
        </div>
      </div>

      {/* TAB 1: CADASTRO */}
      {tab === 'cadastro' && (
        <div className="space-y-3" data-testid="tab-cadastro-content">
          {isAdmin && (
            <div className="flex justify-end">
              <Button size="sm" onClick={() => { setEditTrip(null); setShowForm(true); }} className="bg-cyan-600 hover:bg-cyan-500 h-7 text-xs" data-testid="btn-add-trip">
                <Plus className="w-3 h-3 mr-1" />Cadastrar Tripulante
              </Button>
            </div>
          )}
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-800/60 text-slate-400 text-xs uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">Nome</th>
                  <th className="px-3 py-2 text-left">Funcao</th>
                  <th className="px-3 py-2 text-center">Base</th>
                  <th className="px-3 py-2 text-center">Modelo</th>
                  <th className="px-3 py-2 text-left">Documento</th>
                  <th className="px-3 py-2 text-left">Embarcacao</th>
                  <th className="px-3 py-2 text-center">Status</th>
                  <th className="px-3 py-2 text-center">Tipo</th>
                  <th className="px-3 py-2 text-center">Ult. Embarque</th>
                  {isAdmin && <th className="px-3 py-2 text-center w-20">Acoes</th>}
                </tr></thead>
                <tbody>
                  {filteredTrips.map((t, i) => (
                    <tr key={t.id} className="border-t border-slate-800/50 hover:bg-slate-800/30" data-testid={`trip-row-${i}`}>
                      <td className="px-3 py-2 font-medium text-white">{t.nome}</td>
                      <td className="px-3 py-2 text-slate-300">{t.funcao}</td>
                      <td className="px-3 py-2 text-center"><span className="text-xs px-1.5 py-0.5 rounded bg-slate-700 text-slate-300" translate="no">{t.base}</span></td>
                      <td className="px-3 py-2 text-center font-mono text-xs text-slate-300">{t.modelo_embarque}</td>
                      <td className="px-3 py-2 text-xs text-slate-400 font-mono">{t.documento}</td>
                      <td className="px-3 py-2 text-xs text-slate-300">{t.embarcacao_associada}</td>
                      <td className="px-3 py-2 text-center"><span className={`text-[10px] px-1.5 py-0.5 rounded border font-bold uppercase ${STATUS_COLORS[t.status] || STATUS_COLORS.ativo}`}>{t.status}</span></td>
                      <td className="px-3 py-2 text-center"><span className={`text-[10px] px-1.5 py-0.5 rounded ${t.tipo === 'substituto' ? 'bg-purple-500/15 text-purple-400' : 'bg-blue-500/15 text-blue-400'}`}>{t.tipo || 'fixo'}</span></td>
                      <td className="px-3 py-2 text-center text-xs text-slate-400">{t.data_ultimo_embarque || '-'}</td>
                      {isAdmin && (
                        <td className="px-3 py-2 text-center">
                          <div className="flex gap-1 justify-center">
                            <button onClick={() => { setEditTrip(t); setShowForm(true); }} className="p-1 hover:bg-slate-700 rounded"><Edit2 className="w-3 h-3 text-slate-400" /></button>
                            <button onClick={() => handleDeleteTrip(t.id)} className="p-1 hover:bg-red-900/30 rounded"><Trash2 className="w-3 h-3 text-red-400" /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: ESCALA OPERACIONAL */}
      {tab === 'escala' && (
        <div className="space-y-3" data-testid="tab-escala-content">
          <div className="flex items-center gap-3 bg-slate-800/40 rounded border border-slate-700 p-3">
            <Search className="w-4 h-4 text-slate-400" />
            <input type="date" value={dataFiltro} onChange={e => { setDataFiltro(e.target.value); setPage(1); }} data-testid="filtro-data"
              className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-sm text-white" />
            <select value={setorFiltro} onChange={e => { setSetorFiltro(e.target.value); setPage(1); }} data-testid="filtro-setor"
              className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-sm text-white">
              <option value="">Todos os Setores</option>
              <option value="Navegacao">Navegacao</option>
              <option value="Maquinas">Maquinas</option>
              <option value="Conves">Conves</option>
              <option value="Servicos de Bordo">Servicos de Bordo</option>
            </select>
            <span className="text-xs text-slate-500 ml-auto">{filteredServicos.length} registro(s)</span>
            <div className="flex gap-1">
              <button onClick={() => { const d = new Date(dataFiltro); d.setDate(d.getDate() - 1); setDataFiltro(d.toISOString().slice(0,10)); }} className="p-1 hover:bg-slate-700 rounded"><ChevronLeft className="w-4 h-4 text-slate-400" /></button>
              <button onClick={() => { const d = new Date(dataFiltro); d.setDate(d.getDate() + 1); setDataFiltro(d.toISOString().slice(0,10)); }} className="p-1 hover:bg-slate-700 rounded"><ChevronRight className="w-4 h-4 text-slate-400" /></button>
            </div>
          </div>
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-800/60 text-slate-400 text-xs uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">Data</th><th className="px-3 py-2 text-center">Turno</th><th className="px-3 py-2 text-left">Nome</th><th className="px-3 py-2 text-left">Funcao</th><th className="px-3 py-2 text-center">Base</th><th className="px-3 py-2 text-left">Embarcacao</th><th className="px-3 py-2 text-center">Horario Servico</th><th className="px-3 py-2 text-center">Setor</th><th className="px-3 py-2 text-center">Status</th>
                </tr></thead>
                <tbody>
                  {filteredServicos.map((s, i) => (
                    <tr key={s.id || i} className="border-t border-slate-800/50 hover:bg-slate-800/30">
                      <td className="px-3 py-1.5 text-slate-300 font-mono text-xs">{s.data}</td>
                      <td className="px-3 py-1.5 text-center"><span className={`text-xs px-2 py-0.5 rounded font-bold ${s.turno === 'A' ? 'bg-blue-500/20 text-blue-300' : s.turno === 'B' ? 'bg-amber-500/20 text-amber-300' : s.turno === 'C' ? 'bg-purple-500/20 text-purple-300' : 'bg-emerald-500/20 text-emerald-300'}`}>{s.turno}</span></td>
                      <td className="px-3 py-1.5 font-medium text-white">{s.nome}</td>
                      <td className="px-3 py-1.5 text-slate-300 text-xs">{s.funcao}</td>
                      <td className="px-3 py-1.5 text-center text-xs text-slate-400">{s.base}</td>
                      <td className="px-3 py-1.5 text-xs text-slate-300">{s.embarcacao}</td>
                      <td className="px-3 py-1.5 text-center font-mono text-xs text-cyan-400">{s.horario_servico}</td>
                      <td className="px-3 py-1.5 text-center"><span className={`text-[10px] px-1.5 py-0.5 rounded ${SETOR_COLORS[s.setor] || 'bg-slate-700 text-slate-300'}`}>{s.setor}</span></td>
                      <td className="px-3 py-1.5 text-center"><span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">embarcado</span></td>
                    </tr>
                  ))}
                  {filteredServicos.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-500">Nenhum registro para esta data/site</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: FOLGAS */}
      {tab === 'folgas' && (
        <div className="space-y-3" data-testid="tab-folgas-content">
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg overflow-hidden">
            <div className="p-3 border-b border-slate-800"><h2 className="text-base font-semibold text-white flex items-center gap-2"><Clock className="w-4 h-4 text-amber-400" />Tripulantes em Folga Programada {siteFiltro !== 'Todos' && <span className="text-xs text-cyan-400">({siteFiltro})</span>}</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-800/60 text-slate-400 text-xs uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">Nome</th><th className="px-3 py-2 text-left">Funcao</th><th className="px-3 py-2 text-center">Base</th><th className="px-3 py-2 text-center">Modelo</th><th className="px-3 py-2 text-left">Embarcacao</th><th className="px-3 py-2 text-center">Inicio Folga</th><th className="px-3 py-2 text-center">Fim Folga</th><th className="px-3 py-2 text-center">Dias Restantes</th><th className="px-3 py-2 text-left">Substituido Por</th>
                </tr></thead>
                <tbody>
                  {filteredFolgas.map((f, i) => (
                    <tr key={i} className="border-t border-slate-800/50 hover:bg-slate-800/30">
                      <td className="px-3 py-2 font-medium text-white">{f.nome}</td><td className="px-3 py-2 text-slate-300">{f.funcao}</td><td className="px-3 py-2 text-center text-xs">{f.base}</td><td className="px-3 py-2 text-center font-mono text-xs">{f.modelo}</td><td className="px-3 py-2 text-xs">{f.embarcacao}</td>
                      <td className="px-3 py-2 text-center font-mono text-xs text-amber-400">{f.inicio_folga}</td><td className="px-3 py-2 text-center font-mono text-xs text-emerald-400">{f.fim_folga}</td>
                      <td className="px-3 py-2 text-center"><span className={`font-mono text-sm font-bold ${f.dias_restantes <= 3 ? 'text-red-400' : 'text-amber-400'}`}>{f.dias_restantes}d</span></td>
                      <td className="px-3 py-2 text-xs text-slate-300">{f.substituido_por || '-'}</td>
                    </tr>
                  ))}
                  {filteredFolgas.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-500">Nenhum tripulante em folga{siteFiltro !== 'Todos' ? ` no site ${siteFiltro}` : ''}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: AFASTADOS */}
      {tab === 'afastados' && (
        <div className="space-y-3" data-testid="tab-afastados-content">
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg overflow-hidden">
            <div className="p-3 border-b border-slate-800"><h2 className="text-base font-semibold text-white flex items-center gap-2"><UserX className="w-4 h-4 text-red-400" />Tripulantes Afastados {siteFiltro !== 'Todos' && <span className="text-xs text-cyan-400">({siteFiltro})</span>}</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-800/60 text-slate-400 text-xs uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">Nome</th><th className="px-3 py-2 text-left">Funcao</th><th className="px-3 py-2 text-center">Base</th><th className="px-3 py-2 text-center">Tipo Afastamento</th><th className="px-3 py-2 text-center">Inicio</th><th className="px-3 py-2 text-center">Previsao Retorno</th><th className="px-3 py-2 text-center">Status</th><th className="px-3 py-2 text-left">Substituido Por</th><th className="px-3 py-2 text-left">Documento</th><th className="px-3 py-2 text-center">Anexos</th>
                </tr></thead>
                <tbody>
                  {filteredAfastamentos.map((a, i) => (
                    <tr key={i} className="border-t border-slate-800/50 hover:bg-slate-800/30">
                      <td className="px-3 py-2 font-medium text-white">{a.tripulante_nome}</td><td className="px-3 py-2 text-slate-300">{a.funcao}</td><td className="px-3 py-2 text-center text-xs">{a.base}</td>
                      <td className="px-3 py-2 text-center"><span className="text-xs px-2 py-0.5 rounded bg-red-500/15 text-red-400">{a.tipo_afastamento}</span></td>
                      <td className="px-3 py-2 text-center font-mono text-xs">{a.data_inicio}</td><td className="px-3 py-2 text-center font-mono text-xs">{a.data_previsao_retorno || 'Indefinido'}</td>
                      <td className="px-3 py-2 text-center"><span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">{a.status_retorno}</span></td>
                      <td className="px-3 py-2 text-xs text-slate-300">{a.substituido_por || '-'}</td><td className="px-3 py-2 text-xs text-slate-400">{a.documento_comprobatorio}</td>
                      <td className="px-3 py-2 text-center">
                        <DocumentUploadPanel modulo="escala" entidadeId={a.tripulante_id || a.id} entidadeNome={a.tripulante_nome} compact />
                      </td>
                    </tr>
                  ))}
                  {filteredAfastamentos.length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-500">Nenhum tripulante afastado{siteFiltro !== 'Todos' ? ` no site ${siteFiltro}` : ''}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 5: SUBSTITUICOES */}
      {tab === 'substituicoes' && (
        <div className="space-y-3" data-testid="tab-substituicoes-content">
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg overflow-hidden">
            <div className="p-3 border-b border-slate-800"><h2 className="text-base font-semibold text-white flex items-center gap-2"><ArrowRightLeft className="w-4 h-4 text-purple-400" />Historico de Substituicoes {siteFiltro !== 'Todos' && <span className="text-xs text-cyan-400">({siteFiltro})</span>}</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-800/60 text-slate-400 text-xs uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">Periodo</th><th className="px-3 py-2 text-left">Tripulante Afastado/Folga</th><th className="px-3 py-2 text-left">Funcao</th><th className="px-3 py-2 text-left">Substituido Por</th><th className="px-3 py-2 text-center">Motivo</th><th className="px-3 py-2 text-left">Embarcacao</th><th className="px-3 py-2 text-left">Acao</th>
                </tr></thead>
                <tbody>
                  {filteredSubs.map((s, i) => (
                    <tr key={i} className="border-t border-slate-800/50 hover:bg-slate-800/30">
                      <td className="px-3 py-2 text-xs text-slate-300 font-mono">{s.periodo}</td><td className="px-3 py-2 font-medium text-white">{s.tripulante_afastado}</td><td className="px-3 py-2 text-slate-300">{s.funcao}</td>
                      <td className="px-3 py-2 text-cyan-400 font-medium">{s.substituido_por}</td>
                      <td className="px-3 py-2 text-center"><span className="text-xs px-2 py-0.5 rounded bg-amber-500/15 text-amber-400">{s.motivo}</span></td>
                      <td className="px-3 py-2 text-xs text-slate-300">{s.embarcacao}</td><td className="px-3 py-2 text-xs text-slate-400">{s.acao}</td>
                    </tr>
                  ))}
                  {filteredSubs.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">Nenhuma substituicao{siteFiltro !== 'Todos' ? ` no site ${siteFiltro}` : ''}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* FORM MODAL */}
      {showForm && isAdmin && (
        <TripulanteForm trip={editTrip} onSave={handleSaveTrip} onClose={() => { setShowForm(false); setEditTrip(null); }} token={token} />
      )}
    </div>
  );
}

function TripulanteForm({ trip, onSave, onClose, token }) {
  const [form, setForm] = useState({
    nome: trip?.nome || '', funcao: trip?.funcao || 'Comandante', base: trip?.base || 'RJ',
    modelo_embarque: trip?.modelo_embarque || '28x28', documento: trip?.documento || '',
    embarcacao_associada: trip?.embarcacao_associada || '', status: trip?.status || 'ativo',
    tipo: trip?.tipo || 'fixo', data_ultimo_embarque: trip?.data_ultimo_embarque || '',
    contato_emergencia: trip?.contato_emergencia || '',
    periodo_substituicao_inicio: trip?.periodo_substituicao_inicio || '',
    periodo_substituicao_fim: trip?.periodo_substituicao_fim || '',
  });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeout = useRef(null);

  const handleNameSearch = (value) => {
    setForm(prev => ({ ...prev, nome: value }));
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (value.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    searchTimeout.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/tripulantes/busca-rapida?q=${encodeURIComponent(value)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await r.json();
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      } catch { setSuggestions([]); }
    }, 300);
  };

  const selectSuggestion = (s) => {
    setForm(prev => ({
      ...prev,
      nome: s.nome,
      funcao: s.funcao || prev.funcao,
      embarcacao_associada: s.embarcacao_vinculada || prev.embarcacao_associada,
    }));
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleSubmit = (e) => { e.preventDefault(); onSave(form); };
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="trip-form">
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <h3 className="text-base font-semibold text-white">{trip ? 'Editar' : 'Cadastrar'} Tripulante</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <label className="text-xs text-slate-400">Nome * <span className="text-cyan-500 text-[10px]">(buscar do cadastro)</span></label>
              <input value={form.nome} onChange={e => handleNameSearch(e.target.value)} onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                required autoComplete="off" className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white" data-testid="input-nome" placeholder="Digite para buscar..." />
              {showSuggestions && (
                <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-48 overflow-y-auto" data-testid="suggestions-list">
                  {suggestions.map((s, i) => (
                    <button key={i} type="button" onClick={() => selectSuggestion(s)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-700 border-b border-slate-700/50 transition">
                      <p className="text-sm text-white font-medium">{s.nome}</p>
                      <p className="text-[10px] text-slate-400">{s.funcao} {s.embarcacao_vinculada ? `| ${s.embarcacao_vinculada}` : ''}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div><label className="text-xs text-slate-400">Funcao *</label><select value={form.funcao} onChange={e => set('funcao', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white" data-testid="input-funcao">
              {['Comandante','Imediato','Chefe de Maquinas','Cozinheiro','Marinheiro de Conves','Marinheiro de Maquinas'].map(f => <option key={f} value={f}>{f}</option>)}
            </select></div>
            <div><label className="text-xs text-slate-400">Base *</label><select value={form.base} onChange={e => set('base', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white" data-testid="input-base">
              {[{v:'RJ',l:'Rio de Janeiro'},{v:'PE',l:'Pernambuco'},{v:'PA',l:'Para'}].map(b => <option key={b.v} value={b.v} translate="no">{b.l}</option>)}
            </select></div>
            <div><label className="text-xs text-slate-400">Modelo Embarque *</label><select value={form.modelo_embarque} onChange={e => set('modelo_embarque', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white" data-testid="input-modelo">
              <option value="28x28">28x28</option><option value="40x10">40x10</option><option value="7x7">7x7</option>
            </select></div>
            <div><label className="text-xs text-slate-400">Documento *</label><input value={form.documento} onChange={e => set('documento', e.target.value)} required className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white" data-testid="input-doc" /></div>
            <div><label className="text-xs text-slate-400">Embarcacao * <span className="text-emerald-500 text-[10px]">(auto ao selecionar tripulante)</span></label><input value={form.embarcacao_associada} onChange={e => set('embarcacao_associada', e.target.value)} required className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white" data-testid="input-emb" /></div>
            <div><label className="text-xs text-slate-400">Status</label><select value={form.status} onChange={e => set('status', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white" data-testid="input-status">
              {['ativo','inativo','afastado','em_folga'].map(s => <option key={s} value={s}>{s}</option>)}
            </select></div>
            <div><label className="text-xs text-slate-400">Tipo</label><select value={form.tipo} onChange={e => set('tipo', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white" data-testid="input-tipo">
              <option value="fixo">Fixo</option><option value="substituto">Substituto</option>
            </select></div>
            <div><label className="text-xs text-slate-400">Ult. Embarque</label><input type="date" value={form.data_ultimo_embarque} onChange={e => set('data_ultimo_embarque', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white" /></div>
            <div><label className="text-xs text-slate-400">Contato Emergencia</label><input value={form.contato_emergencia} onChange={e => set('contato_emergencia', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white" /></div>
            {form.tipo === 'substituto' && <>
              <div><label className="text-xs text-slate-400">Inicio Substituicao</label><input type="date" value={form.periodo_substituicao_inicio} onChange={e => set('periodo_substituicao_inicio', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white" /></div>
              <div><label className="text-xs text-slate-400">Fim Substituicao</label><input type="date" value={form.periodo_substituicao_fim} onChange={e => set('periodo_substituicao_fim', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white" /></div>
            </>}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
            <Button type="submit" size="sm" className="bg-cyan-600 hover:bg-cyan-500" data-testid="btn-save-trip">Salvar</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
